import type { EmergencyAccessSessionDetail } from '@usacsmideo/types';
import { groupByDay } from './sessionGrouping';
import { SessionCard, type HandoffConsentProps } from './SessionCard';

/**
 * Renders a flat session list clustered into day groups, newest-day-first
 * (Timeline Filter Rules §D3). Used by both "My Scans" and each family-member
 * group. `focusSessionId` highlights the deep-linked card (CU-86aj1c9ev §2.2).
 */
function formatDayHeader(dayStart: number): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneDay = 24 * 60 * 60 * 1000;
  if (dayStart === today.getTime()) return 'Today';
  if (dayStart === today.getTime() - oneDay) return 'Yesterday';
  return new Date(dayStart).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function DayGroupedSessions({
  sessions,
  focusSessionId,
  now,
  onRefresh,
  isRefreshing,
  dontAskAgain,
  onRecordHandoffConsent,
  isRecordingConsent,
}: {
  sessions: EmergencyAccessSessionDetail[];
  focusSessionId?: string;
  /** CU-86aj3a0k2: forwarded to cards for the live-window refresh button. */
  now?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
} & HandoffConsentProps) {
  if (sessions.length === 0) {
    return <p className="text-sm text-gray-500">No scans yet.</p>;
  }

  const days = groupByDay(sessions);

  return (
    <div className="flex flex-col gap-4">
      {days.map((day) => (
        <div key={day.dayKey} className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {formatDayHeader(day.dayStart)}
          </h4>
          {day.sessions.map((s) => (
            <SessionCard
              key={s.sessionId}
              session={s}
              focused={s.sessionId === focusSessionId}
              now={now}
              onRefresh={onRefresh}
              isRefreshing={isRefreshing}
              dontAskAgain={dontAskAgain}
              onRecordHandoffConsent={onRecordHandoffConsent}
              isRecordingConsent={isRecordingConsent}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default DayGroupedSessions;
